import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import PropTypes from 'prop-types';
import React, { useEffect, useReducer, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { ImageFluid } from './ImageFluid';
import { ImageFixed } from './ImageFixed';
import { ImageFullCentered } from './ImageFullCentered';
const hasWindow = typeof window !== 'undefined';
function parseOptions(options) {
  if (!options) return '';
  return Object.entries(options).filter(_ref => {
    let [, value] = _ref;
    return value !== undefined && value !== null && value !== '';
  }).map(_ref2 => {
    let [key, value] = _ref2;
    return "".concat(key, "=").concat(encodeURIComponent(value));
  }).join('&');
}
function inImageCache(key) {
  try {
    const cache = JSON.parse(window.sessionStorage.getItem('seen_images')) || {};
    return !!cache[key];
  } catch (e) {
    return false;
  }
}
function setImageCache(key) {
  try {
    const cache = JSON.parse(window.sessionStorage.getItem('seen_images')) || {};
    cache[key] = true;
    window.sessionStorage.setItem('seen_images', JSON.stringify(cache));
  } catch (e) {}
}
function createBreakPointsFluid(_ref3) {
  let {
    src,
    parsedOptions,
    fluid,
    retina
  } = _ref3;
  const calculateResolution = value => value * (retina ? 2 : 1);
  const step = fluid.breakpointStep || 150;
  let size = 150;
  const results = [];
  while (size < fluid.maxWidth) {
    results.push("".concat(src, "?w=").concat(calculateResolution(size), "&ar=").concat(fluid.aspectRatio).concat(parsedOptions && "&".concat(parsedOptions), " ").concat(size, "w"));
    size += step;
  }
  results.push("".concat(src, "?w=").concat(calculateResolution(fluid.maxWidth), "&ar=").concat(fluid.aspectRatio).concat(parsedOptions && "&".concat(parsedOptions), " ").concat(fluid.maxWidth, "w"));
  return results.join(',');
}
function createBreakPointsFixed(_ref4) {
  let {
    src,
    parsedOptions,
    fixed,
    retina
  } = _ref4;
  const calculateResolution = value => value * (retina ? 2 : 1);
  const results = [];
  for (let i = 1; i < 3; i += 1) {
    results.push("".concat(src, "?w=").concat(calculateResolution(fixed.width * i), "&h=").concat(calculateResolution(fixed.height * i)).concat(parsedOptions && "&".concat(parsedOptions), " ").concat(i, "x"));
  }
  return results.join(',');
}
function createBreakPointsImageFullCentered(_ref5) {
  let {
    src,
    parsedOptions,
    imageFullCentered,
    retina
  } = _ref5;
  const calculateResolution = value => value * (retina ? 2 : 1);
  const results = [];
  for (let i = 1; i < 3; i += 1) {
    results.push("".concat(src, "?h=").concat(calculateResolution(imageFullCentered.height * i)).concat(parsedOptions && "&".concat(parsedOptions, "&cover=contain"), " ").concat(i, "x"));
  }
  return results.join(',');
}
function init(initialArg) {
  const {
    imageFullCentered,
    fluid,
    fixed,
    src,
    fadeIn,
    options,
    retina
  } = initialArg;
  const calculateResolution = value => value * (retina ? 2 : 1);
  const parsedOptions = parseOptions(options);
  const origin = src.includes('.xceed.me') ? 'xceed' : 'external';
  const key = "".concat(src, "?").concat(imageFullCentered ? "h=".concat(calculateResolution(imageFullCentered.height)).concat(parsedOptions ? "&".concat(parsedOptions) : '', "&cover=contain") : fluid ? "w=".concat(calculateResolution(fluid.maxWidth)) : "w=".concat(calculateResolution(fixed.width), "&h=").concat(calculateResolution(fixed.height))).concat(parsedOptions ? "&".concat(parsedOptions) : '');
  const seenBefore = inImageCache(key);
  let srcSet = '';
  if (origin === 'xceed' && imageFullCentered) {
    srcSet = createBreakPointsImageFullCentered({
      src,
      parsedOptions,
      imageFullCentered,
      retina
    });
  } else if (origin === 'xceed' && fluid) {
    srcSet = createBreakPointsFluid({
      src,
      parsedOptions,
      fluid,
      retina
    });
  } else if (origin === 'xceed' && fixed) {
    srcSet = createBreakPointsFixed({
      src,
      parsedOptions,
      fixed,
      retina
    });
  }
  return {
    seenBefore,
    isVisible: !(!seenBefore && hasWindow && window.IntersectionObserver),
    imgLoaded: !(!seenBefore && hasWindow && window.IntersectionObserver),
    fadeIn,
    parsedOptions,
    srcSet,
    retina,
    origin,
    key
  };
}
function reducer(state, action) {
  return _objectSpread(_objectSpread({}, state), action);
}
export const Image = props => {
  const {
    title,
    alt,
    style = {},
    imgStyle = {},
    placeholderStyle = {},
    fluid,
    fixed,
    imageFullCentered,
    retina,
    src,
    backgroundColor,
    onError,
    onLoad
  } = props;
  const [state, dispatch] = useReducer(reducer, props, init);
  const imageRef = useRef();
  const [ref, inView] = useInView({
    triggerOnce: true,
    rootMargin: '400px 0px'
  });
  const calculateResolution = value => value * (retina ? 2 : 1);
  const bgColor = typeof backgroundColor === 'boolean' ? 'linear-gradient(to right, #e8ecf2, #f5f7fb)' : backgroundColor;
  useEffect(() => {
    const newState = init(props);
    dispatch(newState);
  }, [src, retina]);
  useEffect(() => {
    dispatch({
      isVisible: inView
    });
  }, [inView, src]);
  const handleImageLoaded = () => {
    dispatch({
      imgLoaded: true,
      fadeIn: !state.seenBefore
    });
    setImageCache(state.key);
    if (onLoad) onLoad();
  };
  const imageStyle = _objectSpread({
    opacity: state.imgLoaded || !state.fadeIn ? 1 : 0,
    transition: state.fadeIn ? 'opacity 0.5s' : 'none'
  }, imgStyle);
  const LQIP = state.origin === 'xceed' ? "".concat(src, "?w=28&blur=auto&fm=auto") : '';
  if (imageFullCentered) {
    return React.createElement(ImageFullCentered, {
      ref: ref,
      style: style,
      title: title,
      imageFullCentered: imageFullCentered,
      state: state,
      bgColor: bgColor,
      LQIP: LQIP,
      alt: alt,
      placeholderStyle: placeholderStyle,
      src: src,
      imageRef: imageRef,
      onError: onError,
      handleImageLoaded: handleImageLoaded,
      imageStyle: imageStyle
    });
  } else if (fluid) return React.createElement(ImageFluid, {
    ref: ref,
    style: style,
    title: title,
    fluid: fluid,
    state: state,
    bgColor: bgColor,
    LQIP: LQIP,
    alt: alt,
    imgStyle: imgStyle,
    placeholderStyle: placeholderStyle,
    src: src,
    imageRef: imageRef,
    onError: onError,
    handleImageLoaded: handleImageLoaded,
    imageStyle: imageStyle
  });else if (fixed) return React.createElement(ImageFixed, {
    ref: ref,
    style: style,
    title: title,
    fixed: fixed,
    state: state,
    bgColor: bgColor,
    LQIP: LQIP,
    alt: alt,
    imgStyle: imgStyle,
    placeholderStyle: placeholderStyle,
    src: src,
    imageRef: imageRef,
    onError: onError,
    calculateResolution: calculateResolution,
    handleImageLoaded: handleImageLoaded,
    imageStyle: imageStyle
  });
};
Image.defaultProps = {
  options: {
    q: null,
    fm: 'auto',
    blur: undefined,
    fit: undefined,
    crop: undefined
  },
  alt: ' ',
  onLoad: undefined,
  onError: undefined,
  backgroundColor: false,
  retina: false
};
Image.propTypes = process.env.NODE_ENV !== "production" ? {
  src: PropTypes.string.isRequired,
  fixed: PropTypes.shape({
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
  }),
  fluid: PropTypes.shape({
    maxWidth: PropTypes.number.isRequired,
    aspectRatio: PropTypes.string.isRequired,
    breakpointStep: PropTypes.number
  }),
  retina: PropTypes.bool,
  options: PropTypes.shape({
    q: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    fm: PropTypes.oneOf(['auto', 'jpg', 'png', 'webp']),
    blur: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    fit: PropTypes.oneOf(['cover', 'contain']),
    crop: PropTypes.oneOf(['top', 'bottom', 'left', 'right'])
  }),
  title: PropTypes.string,
  alt: PropTypes.string,
  style: PropTypes.object,
  imgStyle: PropTypes.object,
  placeholderStyle: PropTypes.object,
  backgroundColor: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  onLoad: PropTypes.func,
  onError: PropTypes.func
} : {};